/**
 * Who is on the team.
 *
 * These are real accounts — rows in the `users` table the API signs people in
 * against. The pane used to edit a list inside the settings JSON instead,
 * which meant a "role" set here changed nothing and a person removed here
 * could still sign in. Adding somebody now creates an account they can use;
 * removing one ends the session they are holding.
 *
 * Offline there is no server to hold accounts, so the table says so rather
 * than pretending to manage them.
 */
import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { isOffline } from '../../lib/storage.js';
import api from '../../lib/api.js';
import Icon from '../../lib/icons.jsx';
import { Avatar, Pill, EmptyState, Dash } from '../../components/Primitives.jsx';
import TeamSheet from '../../components/modals/TeamSheet.jsx';
import RolesPane from './RolesPane.jsx';
import { Section } from './SettingsBits.jsx';

/** Owner and Admin administer; the rest are told apart by colour at a glance. */
const ROLE_TONE = {
  Owner: 'p-purple', Admin: 'p-blue', Manager: 'p-cyan', Agent: 'p-green', Viewer: 'p-grey'
};

export default function TeamPane() {
  const { fmtDate, toast } = useApp();
  const [users, setUsers] = useState([]);
  /* Whatever roles this workspace has, including the ones it added. */
  const [roles, setRoles] = useState(['Owner', 'Admin', 'Manager', 'Agent', 'Viewer']);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState('');
  const [sheet, setSheet] = useState(null);

  /* Two things live on this page and only one is wanted at a time: who is on
     the team, and what the roles mean. Team is what people come here for. */
  const [tab, setTab] = useState('team');

  const offline = isOffline();

  const load = useCallback(async () => {
    if (offline) { setLoading(false); return; }
    setLoading(true);
    try {
      const [list, access] = await Promise.all([
        api.auth.users(),
        /* A failure here only costs the custom role names in the picker. */
        api.auth.access().catch(() => null)
      ]);
      setUsers(list);
      if (access && access.names) setRoles(access.names);
      setFailed('');
    } catch (e) {
      setFailed(e?.message || 'Could not load the team');
    }
    setLoading(false);
  }, [offline]);

  useEffect(() => { load(); }, [load]);

  const save = async body => {
    if (sheet && sheet.id) await api.auth.updateUser(sheet.id, body);
    else await api.auth.createUser(body);
    await load();
  };

  const remove = async u => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Remove ${u.name}?\n\nTheir account is deleted and they are signed out immediately. `
      + 'Clients and deals they own keep their name.'
    )) return;
    try {
      await api.auth.deleteUser(u.id);
      toast(`${u.name} removed`);
      await load();
    } catch (e) {
      toast(e?.message || 'Could not remove them');
    }
  };

  const tabs = (
    <div className="tabs settabs">
      <button type="button" className={tab === 'team' ? 'on' : undefined} onClick={() => setTab('team')}>
        Team
      </button>
      <button type="button" className={tab === 'roles' ? 'on' : undefined} onClick={() => setTab('roles')}>
        Roles &amp; permissions
      </button>
    </div>
  );

  if (offline) {
    return (
      <>
        {tabs}
        {tab === 'team' ? (
          <Section title="Team" hint="Who can sign in, and what each of them may do.">
            <EmptyState icon="users" tone="g-purple" title="Not available offline">
              Team accounts live on the server. Sign in to manage them.
            </EmptyState>
          </Section>
        ) : (
          <RolesPane />
        )}
      </>
    );
  }

  return (
    <>
      {tabs}

      {tab === 'team' ? (
      <Section
        title="Team"
        hint="Everyone who can sign in. Removing somebody deletes their account and ends their session; the records they own keep their name."
        action={
          <button type="button" className="btn primary" onClick={() => setSheet({})}>
            <Icon name="plus" />Add a person
          </button>
        }
      >
        {failed ? (
          <EmptyState icon="alert" tone="g-red" title="Could not load the team">
            {failed}
          </EmptyState>
        ) : loading ? (
          <div className="loadrow"><span className="spin" />Loading the team…</div>
        ) : (
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th>Person</th><th>Email</th><th>Role</th><th>Department</th>
                  <th>Status</th><th>Last signed in</th><th />
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div className="cname">
                        <Avatar name={u.name} />
                        <div>
                          <b>{u.name}</b>
                          <small>
                            {u.clients || 0} clients · {u.openDeals || 0} deals
                            {u.title ? ` · ${u.title}` : ''}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td><Pill tone={ROLE_TONE[u.role] || 'p-grey'}>{u.role}</Pill></td>
                    <td>{u.dept || <Dash />}</td>
                    <td>
                      {u.active
                        ? <Pill tone="p-green" icon="check">Active</Pill>
                        : <Pill tone="p-red" icon="lock">Deactivated</Pill>}
                    </td>
                    <td className="dim">
                      {u.lastLoginAt ? fmtDate(new Date(u.lastLoginAt)) : 'Never'}
                    </td>
                    <td>
                      <div className="acts">
                        <button type="button" title="Edit" onClick={() => setSheet(u)}>
                          <Icon name="edit" />
                        </button>
                        <button type="button" className="rm" title="Remove" onClick={() => remove(u)}>
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </Section>
      ) : (
        <RolesPane />
      )}

      {sheet ? (
        <TeamSheet
          key={sheet.id || 'new'}
          roles={roles}
          user={sheet.id ? sheet : null}
          onClose={() => setSheet(null)}
          onSaved={save}
        />
      ) : null}
    </>
  );
}
